import { useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';

type HoverTooltipProps = {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  // 'top' pops the tooltip above the trigger, 'bottom' below. Use 'bottom' when the trigger sits
  // near the top of a scrollable container (e.g. table column headers) so it isn't clipped.
  placement?: 'top' | 'bottom';
};

export function HoverTooltip({
  content,
  children,
  className,
  placement = 'top',
}: HoverTooltipProps): ReactElement {
  const triggerRef = useRef<HTMLSpanElement>(null);
  // Rendered via a portal so the tooltip escapes ancestors with overflow clipping (e.g. the
  // horizontally scrollable table wrapper), which can't be worked around with overflow-y alone.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const showTooltip = () => {
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    setPosition({
      top: placement === 'bottom' ? rect.bottom + 4 : rect.top - 4,
      left: rect.left + rect.width / 2,
    });
  };

  return (
    <span
      ref={triggerRef}
      className={`inline-flex ${className ?? ''}`.trim()}
      onMouseEnter={showTooltip}
      onMouseLeave={() => setPosition(null)}
    >
      {children}
      {position
        ? createPortal(
            <span
              className="fixed z-50 w-max max-w-xs rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg"
              style={{
                top: position.top,
                left: position.left,
                transform: `translate(-50%, ${placement === 'bottom' ? '0%' : '-100%'})`,
              }}
            >
              {content}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}
